import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the incident-reporting start screen. Three
 * short slides (what/who and why a human is the control, what slows an incident
 * down, key terms) shown in the reader while the 3D room downloads. Content only;
 * the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a service-desk report form with the four useful facts filled in  */
/* ------------------------------------------------------------------ */
function ReportFormArt() {
  const H = 296;
  const rows: { label: string; value: string }[] = [
    { label: "What happened", value: "Files locked after a game download" },
    { label: "When", value: "Today, 17:02" },
    { label: "What is affected", value: "My laptop and the shared drive" },
    { label: "What you already did", value: "Disconnected and stopped working" },
  ];
  return (
    <Frame label={tx("Example: incident report")} aria={tx("A service desk form with four fields filled in: what happened, when, what is affected and what you already did")} height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        {tx("Service desk")}
      </text>
      <rect x="236" y="38" width="108" height="20" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="290" y="52" fontSize="10.5" fontWeight="700" fill={C.soft} textAnchor="middle">
        {tx("New incident")}
      </text>
      <line x1="16" y1="70.5" x2="344" y2="70.5" stroke={C.line} />

      {rows.map((r, i) => {
        const y = 80 + i * 42;
        const last = i === rows.length - 1;
        return (
          <g key={r.label}>
            <text x="16" y={y + 4} fontSize="10.5" fill={C.muted}>
              {txv(r.label)}
            </text>
            <rect x="16.5" y={y + 9.5} width="327" height="24" rx="7" fill={C.panel2} stroke={last ? C.mint : C.line} strokeOpacity={last ? 0.6 : 1} />
            <text x="28" y={y + 25.5} fontSize="12" fill={C.text} textLength={last ? 190 : undefined} lengthAdjust="spacingAndGlyphs">
              {txv(r.value)}
            </text>
            {last ? (
              <rect className="sr-anim-blink" x="222" y={y + 14.5} width="1.5" height="14" rx="0.75" fill={C.mint} />
            ) : (
              <g>
                <circle cx="328" cy={y + 21.5} r="7" fill={C.mint} fillOpacity="0.16" />
                <polyline points={`324.5,${y + 21.5} 327,${y + 24} 331.5,${y + 18.8}`} fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            )}
          </g>
        );
      })}

      <rect className="sr-anim-pulse" x="16.5" y="250.5" width="327" height="30" rx="9" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.5" style={{ transformOrigin: "180px 265px" }} />
      <text x="180" y="270" fontSize="12.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Send report now")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. an open VPN joins a personal laptop to shared drives; delay cost */
/* ------------------------------------------------------------------ */
function OpenLinkArt() {
  const H = 298;
  return (
    <Frame label={tx("Example: one open connection")} aria={tx("A personal laptop still connected over a VPN to the shared drives, and a comparison of reporting at once against waiting")} height={H}>
      <rect x="16.5" y="38.5" width="118" height="84" rx="10" fill={C.panel2} stroke={C.red} strokeOpacity="0.5" />
      <rect x="30" y="50" width="90" height="46" rx="5" fill={C.code} stroke={C.line} />
      <text x="75" y="68" fontSize="9.5" fontFamily={MONO} fill={C.red} textAnchor="middle">
        {tx("FILES LOCKED")}
      </text>
      <rect x="44" y="76" width="62" height="5" rx="2.5" fill={C.line} />
      <rect x="44" y="85" width="40" height="5" rx="2.5" fill={C.line} />
      <text x="75" y="113" fontSize="10.5" fill={C.soft} textAnchor="middle">
        {tx("Personal laptop")}
      </text>
      <circle className="sr-anim-ring" cx="120" cy="52" r="4.5" fill="none" stroke={C.red} strokeWidth="1.5" />
      <circle cx="120" cy="52" r="2.2" fill={C.red} />

      <line x1="134" y1="80" x2="226" y2="80" stroke={C.amber} strokeWidth="1.6" strokeDasharray="4 4" />
      <text x="180" y="68" fontSize="10.5" fontWeight="700" fill={C.amber} textAnchor="middle">
        {tx("VPN still open")}
      </text>
      <circle className="sr-anim-float" cx="180" cy="80" r="3.5" fill={C.amber} />

      <rect x="226.5" y="38.5" width="118" height="84" rx="10" fill={C.panel2} stroke={C.line} />
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x="240" y={50 + i * 16} width="90" height="12" rx="4" fill={C.code} stroke={C.line} />
          <rect x="246" y={54 + i * 16} width="40" height="4" rx="2" fill={C.line} />
          <circle cx="321" cy={56 + i * 16} r="2.4" fill={i === 0 ? C.red : C.muted} />
        </g>
      ))}
      <text x="285" y="113" fontSize="10.5" fill={C.soft} textAnchor="middle">
        {tx("Shared drives")}
      </text>

      <line x1="16" y1="138.5" x2="344" y2="138.5" stroke={C.line} />
      <text x="16" y="158" fontSize="11" fill={C.muted}>
        {tx("Reported at once")}
      </text>
      <rect x="16" y="165" width="328" height="10" rx="5" fill={C.panel2} />
      <rect x="16" y="165" width="46" height="10" rx="5" fill={C.mint} />
      <text x="344" y="158" fontSize="10.5" fill={C.mint} textAnchor="end">
        {tx("contained")}
      </text>

      <text x="16" y="198" fontSize="11" fill={C.muted}>
        {tx("Waited to be sure")}
      </text>
      <rect x="16" y="205" width="328" height="10" rx="5" fill={C.panel2} />
      <rect x="16" y="205" width="268" height="10" rx="5" fill={C.red} fillOpacity="0.8" />
      <text x="344" y="198" fontSize="10.5" fill={C.red} textAnchor="end">
        {tx("spreading")}
      </text>

      <rect x="16.5" y="232.5" width="327" height="48" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="238" width="3" height="37" rx="1.5" fill={C.red} />
      <text x="32" y="253" fontSize="12" fontWeight="700" fill={C.red}>
        {tx("Every quiet minute counts")}
      </text>
      <text x="32" y="269" fontSize="11" fill={C.muted}>
        {tx("A vague or late report leaves it connected longer.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary chips plus the four parts of a useful report            */
/* ------------------------------------------------------------------ */
type Glyph = "incident" | "ransom" | "device" | "vpn" | "contain" | "report";

function GlyphShape({ g }: { g: Glyph }) {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "incident":
      return (
        <g {...s}>
          <path d="M0 -8 L8.5 7 H-8.5 Z" />
          <line x1="0" y1="-2.5" x2="0" y2="2" />
          <circle cx="0" cy="4.6" r="0.6" />
        </g>
      );
    case "ransom":
      return (
        <g {...s}>
          <rect x="-6.5" y="-1.5" width="13" height="9" rx="2" />
          <path d="M-3.5 -1.5 V-4.5 a3.5 3.5 0 0 1 7 0 V-1.5" />
        </g>
      );
    case "device":
      return (
        <g {...s}>
          <rect x="-7" y="-6.5" width="14" height="9.5" rx="1.6" />
          <line x1="-9.5" y1="6" x2="9.5" y2="6" />
        </g>
      );
    case "vpn":
      return (
        <g {...s}>
          <circle cx="-7" cy="0" r="2.4" />
          <circle cx="7" cy="0" r="2.4" />
          <line x1="-4.2" y1="0" x2="4.2" y2="0" strokeDasharray="1.8 2.2" />
        </g>
      );
    case "contain":
      return (
        <g {...s}>
          <line x1="-9.5" y1="0" x2="-3.5" y2="0" />
          <line x1="3.5" y1="0" x2="9.5" y2="0" />
          <line x1="-1.2" y1="-5.5" x2="1.2" y2="5.5" />
        </g>
      );
    case "report":
      return (
        <g {...s}>
          <rect x="-6" y="-8" width="12" height="16" rx="2" />
          <polyline points="-3,0 -0.8,2.4 3.2,-2.6" />
          <line x1="-3" y1="5" x2="1" y2="5" />
        </g>
      );
  }
}

const CHIPS: { g: Glyph; title: string; sub: string }[] = [
  { g: "incident", title: "Incident", sub: "puts data at risk" },
  { g: "ransom", title: "Ransomware", sub: "locks your files" },
  { g: "device", title: "Personal device", sub: "your own laptop" },
  { g: "vpn", title: "VPN", sub: "link to the office" },
  { g: "contain", title: "Containment", sub: "cut the connection" },
  { g: "report", title: "Report", sub: "facts, not blame" },
];

const FACTS = ["What", "When", "Affected", "Done"];

function TermsArt() {
  const H = 298;
  return (
    <Frame label={tx("Terms at a glance")} aria={tx("Six terms as labelled icon chips: incident, ransomware, personal device, VPN, containment and report, plus the four parts of a useful report")} height={H}>
      {CHIPS.map((c, i) => {
        const col = i % 2;
        const row = Math.floor(i / 2);
        return (
          <g key={c.g} transform={`translate(${16 + col * 172} ${38 + row * 62})`}>
            <rect x="0.5" y="0.5" width="155" height="51" rx="10" fill={C.panel2} stroke={C.line} />
            <circle cx="26" cy="26" r="16" fill={C.mint} fillOpacity="0.12" />
            {i === 0 ? <circle className="sr-anim-ring" cx="26" cy="26" r="16" fill="none" stroke={C.mint} strokeWidth="1.2" /> : null}
            <g transform="translate(26 26)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="23" fontSize="12.5" fontWeight="700" fill={C.text}>
              {txv(c.title)}
            </text>
            <text x="51" y="39" fontSize="10.5" fill={C.muted}>
              {txv(c.sub)}
            </text>
          </g>
        );
      })}

      <rect x="16.5" y="228.5" width="327" height="56" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="30" y="246" fontSize="10.5" fill={C.muted}>
        {tx("A useful report says")}
      </text>
      {FACTS.map((f, i) => (
        <g key={f}>
          <rect
            className={i === 0 ? "sr-anim-pulse" : undefined}
            x={30 + i * 76}
            y="254"
            width="68"
            height="22"
            rx="11"
            fill={C.mint}
            fillOpacity="0.14"
            stroke={C.mint}
            strokeOpacity="0.45"
            style={i === 0 ? { transformOrigin: `${64 + i * 76}px 265px` } : undefined}
          />
          <text x={64 + i * 76} y="269" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
            {txv(f)}
          </text>
        </g>
      ))}
    </Frame>
  );
}

export const INCIDENT_REPORTING_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Report it early. Report it plainly.",
    illustration: ReportFormArt,
    caption: "Four facts turn a worry into something IT can act on.",
    sections: [
      {
        h: "What this is about",
        body: "A download on your own laptop goes wrong and reaches the office. You see what happens, then write the report that starts the response.",
      },
      {
        h: "Who it\u2019s for",
        body: "All staff. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "IT cannot fix what it does not know about. You are often the first to notice something wrong, and a quick, clear report is what lets them contain it.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "What slows an incident down.",
    illustration: OpenLinkArt,
    caption: "An open work connection makes a personal device part of the office.",
    sections: [
      {
        h: "Waiting until you are sure",
        body: "You do not need proof. Report what you saw as soon as you notice it, even if it may be nothing.",
      },
      {
        h: "Fixing it alone",
        body: "Restarting, deleting files or paying a ransom can make it worse and wipe the clues IT needs.",
      },
      {
        h: "Thinking a personal laptop is safe",
        body: "If a work connection such as the VPN is still open, malware can reach the same shared drives as your work PC.",
      },
      {
        h: "A vague or calm report",
        body: "No time and no affected systems, or \u201cdon\u2019t worry\u201d, and IT may treat it as minor and leave it connected.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Security incident", body: "Anything that puts company systems or information at risk: a strange download, a locked screen, a message you should not have opened." },
      { h: "Ransomware", body: "Malware that locks your files and demands payment to give them back." },
      { h: "Personal device", body: "Your own laptop or phone. It becomes a company risk the moment it has a work connection open." },
      { h: "VPN", body: "A secure link from a device to the company network. While it is open, the device can reach company files." },
      { h: "Containment", body: "Stopping an incident from spreading, for example by disconnecting the device. Then you report it." },
      { h: "Incident report", body: "A short note saying what happened, when, what is affected and what you already did." },
    ],
  },
];
